import React, { useState, useEffect } from 'react'
import { X, Clock, Calendar, ChevronDown, ChevronUp } from 'lucide-react'
import { CurrentWeekEntries, timesheetUtils, SubmitTimesheetRequest } from '@/services/timesheetService'

interface SubmitTimesheetModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: SubmitTimesheetRequest) => Promise<void>
  weekEntries: CurrentWeekEntries | null
  loading?: boolean
}

const SubmitTimesheetModal: React.FC<SubmitTimesheetModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  weekEntries,
  loading = false
}) => {
  const [summary, setSummary] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setSummary('')
      setIsExpanded(false)
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!weekEntries) return

    setIsSubmitting(true)
    try {
      await onSubmit({ summary: summary.trim() || undefined })
      onClose()
    } catch (error) {
      console.error('Submit timesheet error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen || !weekEntries) return null

  const weekRange = timesheetUtils.formatWeekRange(weekEntries.weekStart, weekEntries.weekEnd)
  const entriesByDate = timesheetUtils.groupEntriesByDate(weekEntries.entries)
  const sortedDates = Object.keys(entriesByDate).sort()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">提交本週工時申報</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Week Range */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-gray-600">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">申報週次：{weekRange}</span>
            </div>
          </div>

          {/* Statistics Overview */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">📊 工時統計總覽</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-600">總工時</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {timesheetUtils.convertSecondsToHours(weekEntries.entries.reduce((sum, entry) => sum + entry.duration, 0)).toFixed(2)} 小時
                  </div>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600">工作天數</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {weekEntries.workingDays} 天
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  🏢 涉及專案：{weekEntries.projectsInvolved.join('、')} ({weekEntries.projectsInvolved.length}個)
                </span>
              </div>
            </div>
          </div>

          {/* Time Entries Preview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">📋 工時明細預覽</h3>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <span className="text-sm">{isExpanded ? '收起詳細' : '展開詳細'}</span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {sortedDates.map(date => {
                const dayEntries = entriesByDate[date]
                const dayTotal = timesheetUtils.calculateDayTotalHours(dayEntries)
                const dateDisplay = timesheetUtils.formatDateDisplay(date)

                return (
                  <div key={date} className="bg-white rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        📅 {dateDisplay}
                      </span>
                      <span className="text-sm text-gray-600">
                        總計 {dayTotal.toFixed(2)} 小時
                      </span>
                    </div>

                    {isExpanded ? (
                      <div className="space-y-2">
                        {dayEntries.map(entry => (
                          <div key={entry.id} className="bg-gray-50 rounded p-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">
                                  🏢 {entry.project.name}
                                </span>
                                <span className="text-sm text-gray-600 ml-2">
                                  ({timesheetUtils.convertSecondsToHours(entry.duration).toFixed(2)}h)
                                </span>
                              </div>
                            </div>
                            {entry.description && (
                              <div className="mt-1 text-sm text-gray-600">
                                📝 {entry.description}
                              </div>
                            )}
                            <div className="mt-1 text-xs text-gray-500">
                              {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })} - {entry.endTime ? new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              }) : '進行中'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {dayEntries.map(entry => (
                          <div key={entry.id} className="text-sm text-gray-600">
                            • {entry.project.name} ({timesheetUtils.convertSecondsToHours(entry.duration).toFixed(2)}h) - {entry.description || '無描述'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Work Summary */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">📝 本週工作總結 (選填)</h3>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="請描述本週的主要工作內容、完成的任務、遇到的問題以及下週的計劃..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              disabled={isSubmitting}
            />
            <div className="mt-2 text-sm text-gray-500">
              💡 提示：詳細的工作總結有助於主管了解您的工作內容，提高審核效率
            </div>
          </div>

          {/* Reminders */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">⚠️ 重要提醒</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start space-x-2">
                  <span className="text-yellow-600">📌</span>
                  <span><strong>提交前請確認：</strong></span>
                </div>
                <ul className="ml-6 space-y-1">
                  <li>• ✅ 工時記錄完整且正確</li>
                  <li>• ✅ 專案分類準確</li>
                  <li>• ✅ 工作描述清楚</li>
                </ul>
                
                <div className="flex items-start space-x-2 mt-3">
                  <span className="text-blue-600">📋</span>
                  <span><strong>提交後：</strong></span>
                </div>
                <ul className="ml-6 space-y-1">
                  <li>• 🔒 無法再修改本週工時</li>
                  <li>• 👨‍💼 將在週一對齊會議時審核</li>
                  <li>• 📧 回饋結過將透過系統通知</li>
                </ul>

                <div className="flex items-start space-x-2 mt-3">
                  <span className="text-green-600">🆘</span>
                  <span><strong>如有問題請聯繫主理人</strong></span>
                </div>
                <ul className="ml-6 space-y-1">
                  <li>電子郵件 ceo@foreverwebs.com 或 Slack 私訊</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-ghost"
            disabled={isSubmitting}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={isSubmitting || loading}
          >
            {isSubmitting ? '提交中...' : '確認提交'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SubmitTimesheetModal
